import { UrlParams } from 'types/urlParams';
import {
  PreviewEntity,
  isAuthenticatedPreviewRoute,
  isCollabMapEntity,
  isEngagementLike,
  isExternalPreviewRoute,
  supportsExternalPreview,
  supportsPublishedShare,
} from '@/constants/previewEntities';

/** Every string the preview banner renders. The locale parity spec requires each in every locale. */
export const PREVIEW_BANNER_KEYS = {
  latest: 'survey.previewLatest',
  shared: 'survey.previewShared',
  published: 'survey.previewPublished',
  publishedScheduled: 'survey.previewPublishedScheduled',
  sharedPublished: 'survey.previewSharedPublished',
  sharedScheduled: 'survey.previewSharedScheduled',
  noResponses: 'survey.previewNoResponses',
  noContributions: 'survey.previewNoContributions',
  sharedExpiry: 'survey.previewSharedExpiry',
  sharedExpiryUnknown: 'survey.previewSharedExpiryUnknown',
  copyLink: 'survey.previewCopyLink',
  linkCopied: 'survey.previewLinkCopied',
  copied: 'survey.previewCopied',
  copyFailed: 'survey.previewCopyFailed',
  noticeLabel: 'survey.previewNoticeLabel',
  dismiss: 'survey.previewDismiss',
} as const;

type PreviewBannerKey = (typeof PREVIEW_BANNER_KEYS)[keyof typeof PREVIEW_BANNER_KEYS];

/**
 * Which preview the visitor is on, by the data it shows. The authenticated `/preview` and the shared
 * `/externalPreview` show the latest saved version; with `previewLink=true` either shows the
 * published version instead, as does the public URL. Nothing is collected on any of them.
 */
export type PreviewView = 'authenticated' | 'external' | 'publishedLink' | 'externalPublished';

export const resolvePreviewView = (slug?: string[], urlParams?: UrlParams): PreviewView => {
  const shared = isExternalPreviewRoute(slug);
  if (urlParams?.previewLink) {
    return shared ? 'externalPublished' : 'publishedLink';
  }
  return shared ? 'external' : 'authenticated';
};

const SHARED_VIEWS: readonly PreviewView[] = ['external', 'externalPublished'];

/** The message per view, and per published view the variant naming its release date. */
const MESSAGE_KEYS: Record<PreviewView, { undated: PreviewBannerKey; scheduled?: PreviewBannerKey }> = {
  authenticated: { undated: PREVIEW_BANNER_KEYS.latest },
  external: { undated: PREVIEW_BANNER_KEYS.shared },
  publishedLink: { undated: PREVIEW_BANNER_KEYS.published, scheduled: PREVIEW_BANNER_KEYS.publishedScheduled },
  externalPublished: { undated: PREVIEW_BANNER_KEYS.sharedPublished, scheduled: PREVIEW_BANNER_KEYS.sharedScheduled },
};

/** What the preview leaves uncollected. Content pages collect nothing, so they say nothing. */
const collectionNoticeKey = (entity: PreviewEntity, hasAuth0Token: boolean): PreviewBannerKey | undefined => {
  if (isCollabMapEntity(entity) && !hasAuth0Token) {
    return PREVIEW_BANNER_KEYS.noContributions;
  }
  if (isEngagementLike(entity) || isCollabMapEntity(entity)) {
    return PREVIEW_BANNER_KEYS.noResponses;
  }
  return undefined;
};

interface PreviewBannerCopyOptions {
  view: PreviewView;
  entity: PreviewEntity;
  slug?: string[];
  hasAuth0Token: boolean;
  hasShareCode: boolean;
  /** The page goes live later, and its release date is known. */
  hasReleaseDate: boolean;
}

export interface PreviewBannerCopy {
  messageKey: PreviewBannerKey;
  /** Whether `messageKey` interpolates the release date. */
  namesReleaseDate: boolean;
  collectionNoticeKey?: PreviewBannerKey;
  /**
   * Only on an authenticated route of a shareable entity, once the gateway minted a code: the live
   * build, or the published version of an entity that supports a published share.
   */
  canShare: boolean;
  /** The shared views keep their banner: it is the only sign that this is not the live page. */
  canDismiss: boolean;
  /** The shared views say when their link stops working; nowhere else describes a link's validity. */
  showsLinkExpiry: boolean;
}

export const resolvePreviewBannerCopy = ({
  view,
  entity,
  slug,
  hasAuth0Token,
  hasShareCode,
  hasReleaseDate,
}: PreviewBannerCopyOptions): PreviewBannerCopy => {
  const { undated, scheduled } = MESSAGE_KEYS[view];
  const namesReleaseDate = Boolean(scheduled) && hasReleaseDate;
  return {
    messageKey: namesReleaseDate && scheduled ? scheduled : undated,
    namesReleaseDate,
    collectionNoticeKey: collectionNoticeKey(entity, hasAuth0Token),
    canShare:
      (view === 'authenticated' || (view === 'publishedLink' && supportsPublishedShare(entity))) &&
      supportsExternalPreview(entity) &&
      isAuthenticatedPreviewRoute(slug) &&
      hasShareCode,
    canDismiss: !SHARED_VIEWS.includes(view),
    showsLinkExpiry: SHARED_VIEWS.includes(view),
  };
};
